import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import AuthShell from "./AuthShell";
import GoogleButton from "@/components/auth/GoogleButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", form);
      track("Signed Up", { method: "password" });
      setUser(data);
      // Fresh accounts always run through onboarding first (unless admin).
      const dest = data.onboarding_complete || data.role === "admin"
        ? (location.state?.from || "/dashboard")
        : "/onboarding";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Create your Studio account"
      subtitle={<>Welcome to <span className="italic text-aurora">GoldKey.</span></>}
      footer={
        <span className="text-xs text-neutral-500 tracking-[0.18em] uppercase">
          Have an account?{" "}
          <Link to="/login" className="text-gold hover:text-gold" data-testid="signup-to-login">
            Log in
          </Link>
        </span>
      }
    >
      <GoogleButton label="Sign up with Google" testid="signup-google" />

      <div className="relative my-6 sm:my-8 text-center">
        <div className="absolute inset-x-0 top-1/2 h-px bg-neutral-100" />
        <span className="relative bg-white px-3 text-[10px] uppercase tracking-[0.28em] text-neutral-400">
          or with email
        </span>
      </div>

      <form onSubmit={submit} data-testid="signup-form" className="space-y-6">
        <div>
          <label className="overline block mb-2" htmlFor="sg-name">Your name</label>
          <input
            id="sg-name"
            data-testid="signup-name"
            required
            value={form.name}
            onChange={update("name")}
            className="editorial-input"
            placeholder="Jane Doe"
            autoComplete="name"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="sg-email">Work email</label>
          <input
            id="sg-email"
            data-testid="signup-email"
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            className="editorial-input"
            placeholder="jane@company.com"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="sg-password">Password</label>
          <input
            id="sg-password"
            data-testid="signup-password"
            required
            type="password"
            minLength={8}
            value={form.password}
            onChange={update("password")}
            className="editorial-input"
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />
        </div>

        {error && (
          <p data-testid="signup-error" className="text-sm text-rose-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="btn-aurora w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="signup-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Creating…
            </>
          ) : (
            <>
              Create my account <ArrowUpRight size={16} />
            </>
          )}
        </button>

        <p className="text-xs text-neutral-400 leading-relaxed">
          By creating an account you accept our terms. New customers please pick a plan first - see <Link to="/pricing" className="text-gold hover:text-gold">pricing</Link>.
        </p>
      </form>
    </AuthShell>
  );
}
